// const { unitlessProperties } = require('../../../../scripts/webpackPlugin/constants');
import { isTransformProperty, normalizeTransformValue } from './transformValue';
// 无需px单位的样式
const unitlessProperties = [
  // CSS 原生属性名（kebab-case）
  'opacity',
  'z-index',
  'font-weight',
  'flex-grow',
  'flex-shrink',
  'max-lines',
  'flex',
  'iterations',
  'animation-iteration-count',
  '-webkit-line-clamp',
  'order',
  'tab-size',
  'column-count',
  // React 驼峰样式属性名（camelCase）
  'zIndex',
  'fontWeight',
  'flexGrow',
  'flexShrink',
  'maxLines',
  'animationIterationCount',
  'tabSize',
  'columnCount',
];

// background-color 不支持的属性值
const backgroundColorUnsupports = [
  'linear-gradient',
  'radial-gradient',
  'conic-gradient',
  'repeating-linear-gradient',
  'repeating-radial-gradient',
  'repeating-conic-gradient',
  'url',
  'image-set',
  'element',
  '-webkit-linear-gradient',
  '-moz-linear-gradient',
  '-o-linear-gradient',
  '-ms-linear-gradient',
];

// webcomponent获取react内部属性
export function getReactProps(webCompThis: any): any {
  const propsKeyPrefix = '__reactProps';
  const propsKey = Object.keys(webCompThis).find((k) => k.startsWith(propsKeyPrefix)) || '';
  return webCompThis[propsKey];
}

// 驼峰转小写蛇形（中横线连接），更通用，可处理包含数字的命名场景
export function toDashedCase(camelCase: string): string {
  return camelCase.replace(/([a-z0-9])([A-Z])/g, (_, a, b) => `${a}-${b.toLowerCase()}`);
}

// 驼峰转小写蛇形（下划线线连接），更通用，可处理包含数字的命名场景
export function toBottomDashedCase(camelCase: string): string {
  try {
    const res = camelCase.replace(/([a-z0-9])([A-Z])/g, (_, a, b) => `${a}_${b.toLowerCase()}`);
    return res;
  } catch (e) {
    return camelCase;
  }
}

// 蛇形（横线连接）转驼峰
export function toCamelCase(dashedCase: string): string {
  return dashedCase.replace(/-([a-z])/g, (_, b) => `${b.toUpperCase()}`);
}

// 驼峰装小写蛇形（横线连接），用于 CSS 样式属性转换，适合标准驼峰命名
export function toKebabCase(str) {
  const kebabCase = str
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();

  return /^(webkit|moz|ms|o)-/.test(kebabCase)
    ? `-${kebabCase}`
    : kebabCase;
}

// 样式添加单位：height: 100; => height: 100px;
// 123
export function styleAddUnit(decl: { prop: string; value: any; }) {
  if (
    unitlessProperties.includes(decl.prop) // 无单位属性跳过
    || decl.value === undefined
    || decl.value === null
  ) {
    return decl.value;
  }
  if (isTransformProperty(decl.prop)) {
    return normalizeTransformValue(decl.value);
  }

  // console.log('------------styleAddUnit---------', decl.value)
  const valueArray = (String(decl.value) || '').split(' ');
  return valueArray
    .map((val) => {
      if (val && !Number.isNaN(+val) && val !== '0') {
        // 检查值是否为数字且不是0
        return `${val}px`; // 添加 px 单位
      }
      if (val && String(val).includes('dp')) {
        // dp替换为 px 单位 TODO,移动端另外插件再转vh vw
        return String(val).replace('dp', 'px');
      }
      return val;
    })
    .join(' ');
}

// 结合样式属性值，修正样式key
// key支持CSS 原生属性名 和 React 驼峰样式属性名
// 支持所有react驼峰样式转换逻辑, 以及machpro特有样式key逻辑，如：maxLines
export function convertStyleKeyVal(key, value) {
  let correctedKey = key;
  let correctedValue = value;
  if (
    (key === 'background-color' || key === 'backgroundColor')
    && typeof value === 'string'
    && backgroundColorUnsupports.some((it) => value.includes(it))
  ) {
    // 样式属性校正：background-color 包含 linear-gradient 时改为 background
    correctedKey = 'background';
  }

  if (
    (key === 'background-image' || key === 'backgroundImage')
    && typeof value === 'string'
    && value === 'transparent'
  ) {
    // 样式属性校正：background-image值为transparent 时改为 unset
    correctedValue = 'unset';
  }

  // text行数样式兼容: maxLinesTransformer.js
  // if (decl.value === 0 || decl.value === '0' || decl.value === 'none') realVal = '2000000000';
  if (key === 'maxLines' || key === 'max-lines') {
    correctedKey = '-webkit-line-clamp';
    if (value === 0 || value === '0' || value === 'none') correctedValue = '2000000000';
  }

  correctedValue = styleAddUnit({ prop: correctedKey, value: correctedValue });
  return [correctedKey, correctedValue];
}

// 样式对象转字符串
export function convertToInlineStyle(styles) {
  return Object.entries(styles)
    .map(([key, value]) => {
      // 样式属性和样式值校正
      const [correctedKey, correctedValue] = convertStyleKeyVal(key, value);
      return `${toKebabCase(correctedKey)}:${correctedValue};`;
    })
    .join('');
}

// 样式字符串转对象（不改变样式大小写和驼峰形式）
export function parseStyleString(styleString: string): Record<string, string> {
  const styleObject: Record<string, string> = {};
  // 如果 styleString 是空字符串，直接返回空对象
  if (!styleString.trim()) {
    return styleObject;
  }

  // 按分号分割每个样式声明
  const stylePairs: string[] = styleString.split(';');

  stylePairs.forEach((pair) => {
    // 按冒号分割属性名和值，并去除多余的空格
    const [key = '', value = ''] = pair.split(':').map((item: string) => item.trim());
    // 确保 key 和 value 都存在
    if (key && value) {
      styleObject[key] = value;
    }
  });
  return styleObject;
}

export interface KeyFrames {
  from: { [key in string]: any };
  to: { [key in string]: any };
}

export interface AnimationCallbackValue {
  state: 'start' | 'end';
}

export interface Animation {
  duration: string;
  iterationCount: number | 'infinite';
  direction: 'normal' | 'reverse' | 'alternate' | 'alternate-reverse';
  timingFunction: string;
  fillMode: FillMode | 'forward';
  transformOrigin: string;
  delay: string;
  keyFrames: KeyFrames;
}

// machpro动画样式，获取添加单位后的keyframes数组
// {from: {transform: "translateY(-60px)"}}
export const processKeyFrames = (keyFrames: KeyFrames): Keyframe[] | PropertyIndexedKeyframes | null =>
  Object.entries(keyFrames).map(([key, frame]) => {
    const styles = { ...frame };
    Object.entries(styles).forEach(([prop, value]) => {
      styles[prop] = styleAddUnit({ prop, value });
    });
    return styles;
  });

// machpro动画样式，动画次数属性转换
export const parseTimeString = (duration: number | string): number | undefined => {
  if (typeof duration === 'number') {
    return duration; // 已经是数字，直接返回
  }
  if (!duration) {
    return undefined; // 字符串为空，返回undefined
  }
  const match = duration.match(/^(\d+)(ms|s)$/);
  if (match) {
    const value = parseFloat(match[1]);
    const unit = match[2];
    if (unit === 's') {
      return value * 1000; // 秒转换为毫秒
    } else if (unit === 'ms') {
      return value; // 毫秒直接返回
    }
  }
  return 0;
};

// machpro动画演示转换
export const processMachAnimation = (animationOptions: Animation) => {
  const {
    duration, iterationCount, direction, timingFunction, delay, fillMode
  } = animationOptions;
  const keyframes = processKeyFrames(animationOptions.keyFrames);
  const iterations = iterationCount === 'infinite' ? Number.POSITIVE_INFINITY : iterationCount;
  const fill = fillMode === 'forward' ? 'forwards' : fillMode;
  const options: number | KeyframeAnimationOptions | undefined = {
    duration: parseTimeString(duration),
    iterations: iterationCount === 'infinite' ? Number.POSITIVE_INFINITY : iterationCount,
    direction,
    easing: timingFunction,
    fill,
    // origin: transformOrigin,
    delay: parseTimeString(delay),
  };
  return { keyframes, options };
};

/**
 * 为DOM节点增加或修改行内样式
 * @param {element} element - 目标DOM节点
 * @param {string} styleName - 要设置的样式属性名称
 * @param {string} value - 要设置的样式值
 */
export const setInlineStyle = (params: { element: HTMLElement; styleName: string; value: string | null; }) => {
  const { element, styleName, value } = params || {};
  if (!element || !styleName) return;

  // 格式化样式，增加单位等
  // const styleValue = formatStyleValue({ key: styleName, value });
  const [correctedKey, correctedValue] = convertStyleKeyVal(styleName, value);

  // 将样式属性名称转换为驼峰命名法（例如，'background-color' 转换为 'backgroundColor'）
  const camelCaseStyleName = toCamelCase(correctedKey);

  // 设置行内样式
  element.style[camelCaseStyleName] = correctedValue;
};

export function getSplitArr(str) {
  const trimStr = String(str || '').trim();
  return trimStr ? trimStr.split(' ') : [];
}

// img-ap-hongkong.mykeeta.net
// img-eu.mykeeta.net
// /sailorproduct
// /sailorfempcpublic
// /picturesupload
// jpg/png
export function shouldWebpFormat(str) {
  const trimStr = String(str || '').trim();
  return (
    trimStr.includes('//img-ap-hongkong.mykeeta.net')
    || trimStr.includes('//img-ap-hongkong.inf.test.sankuai.com')
    || trimStr.includes('//img-eu.mykeeta.net'))
    // && (
    // trimStr.includes('/sailorproduct')
    //   || trimStr.includes('/sailorbanmore'))
    // || trimStr.includes('/sailorfempcpublic')
    // // || trimStr.includes('/picturesupload')
    && (
      trimStr.includes('.jpg')
    || trimStr.includes('.png')
    );
}

// 链接上包含venus处理逻辑： @limit_200k | @format=webp | @style=keeta1 | @style=keeta2等
export function hasVenusFormat(str) {
  const trimStr = String(str || '').trim();
  return trimStr.includes('@limit_')
    || trimStr.includes('@format=')
    || trimStr.includes('@style=');
}
